/**
 * Counteract Windows OS display scaling so the SPA lays out the same at
 * 100% / 125% / 150% / 200% system scale — without cancelling browser zoom.
 *
 * `window.devicePixelRatio` includes both OS DPI and Ctrl+/- page zoom.
 * We compensate using the screen's OS DPR only (Window Management API when
 * available), so browser zoom still enlarges fonts while layout ratios stay
 * stable and flex boxes can grow to fit text.
 *
 * Layout: expand `#os-scale-root` to `osDpr × 100%` then `scale(1/osDpr)`.
 * `--os-vh` / `--os-vw` track that compensated layout box. Apple platforms
 * are left alone (Retina DPR ≠ Windows UI scaling).
 */

export const OS_SCALE_ROOT_ID = "os-scale-root";

export type OsScaleNavigatorHints = {
  userAgent?: string;
  platform?: string;
  userAgentDataPlatform?: string;
};

type ScreenDetailedLike = {
  devicePixelRatio?: number;
};

type ScreenDetailsLike = {
  currentScreen: ScreenDetailedLike;
  addEventListener?: (
    type: string,
    listener: () => void,
  ) => void;
  removeEventListener?: (
    type: string,
    listener: () => void,
  ) => void;
};

/** Common Windows display-scale steps — used only for coarse sync fallbacks. */
const OS_SCALE_STEPS = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 3];

export function shouldCompensateOsScale(
  hints: OsScaleNavigatorHints = {},
): boolean {
  const platform = hints.userAgentDataPlatform ?? hints.platform ?? "";
  const userAgent = hints.userAgent ?? "";
  // navigator.platform is "Win32" on 64-bit Windows — match Win*, not only "Windows".
  return (
    /Win/i.test(platform) ||
    /Windows/i.test(userAgent) ||
    /Windows/i.test(platform)
  );
}

export function osScaleFactor(
  osDevicePixelRatio: number,
  compensate: boolean,
): number {
  if (!compensate) return 1;
  const dpr =
    Number.isFinite(osDevicePixelRatio) && osDevicePixelRatio > 0
      ? osDevicePixelRatio
      : 1;
  return dpr;
}

/** browserZoom ≈ window.devicePixelRatio / osDevicePixelRatio */
export function browserZoomFactor(
  windowDevicePixelRatio: number,
  osDevicePixelRatio: number,
): number {
  const windowDpr =
    Number.isFinite(windowDevicePixelRatio) && windowDevicePixelRatio > 0
      ? windowDevicePixelRatio
      : 1;
  const osDpr =
    Number.isFinite(osDevicePixelRatio) && osDevicePixelRatio > 0
      ? osDevicePixelRatio
      : 1;
  return windowDpr / osDpr;
}

/**
 * Sync fallback when Screen Details is unavailable: strip a coarse
 * outer/inner zoom guess, then snap toward common Windows OS scales.
 */
export function estimateOsDevicePixelRatio(
  windowDevicePixelRatio: number,
  outerWidth: number,
  innerWidth: number,
): number {
  const windowDpr =
    Number.isFinite(windowDevicePixelRatio) && windowDevicePixelRatio > 0
      ? windowDevicePixelRatio
      : 1;
  if (innerWidth <= 0) return windowDpr;

  const zoomGuess = outerWidth / innerWidth;
  // Browser chrome makes outer/inner noisy — only trust a moderate band.
  let os = windowDpr;
  if (Number.isFinite(zoomGuess) && zoomGuess >= 0.75 && zoomGuess <= 2.5) {
    os = windowDpr / zoomGuess;
  }

  let best = OS_SCALE_STEPS[0]!;
  let bestDist = Math.abs(os - best);
  for (const step of OS_SCALE_STEPS) {
    const dist = Math.abs(os - step);
    if (dist < bestDist) {
      best = step;
      bestDist = dist;
    }
  }
  // Prefer a known Windows step when we're close; otherwise keep the raw estimate.
  return bestDist <= 0.08 ? best : os;
}

export function readNavigatorHints(
  nav: Navigator = navigator,
): OsScaleNavigatorHints {
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    userAgentDataPlatform: (
      nav as Navigator & { userAgentData?: { platform?: string } }
    ).userAgentData?.platform,
  };
}

function clearScaleStyles(el: HTMLElement) {
  el.style.removeProperty("transform");
  el.style.removeProperty("transform-origin");
  el.style.removeProperty("width");
  el.style.removeProperty("height");
  el.style.removeProperty("zoom");
}

function setViewportUnitVars(osDpr: number) {
  const root = document.documentElement;
  if (osDpr === 1) {
    root.style.removeProperty("--os-vh");
    root.style.removeProperty("--os-vw");
    return;
  }
  // 1 unit == 1% of the *OS-compensated* layout viewport.
  root.style.setProperty("--os-vh", `${(window.innerHeight * osDpr) / 100}px`);
  root.style.setProperty("--os-vw", `${(window.innerWidth * osDpr) / 100}px`);
}

/** Cached OS DPR from Screen Details (excludes browser zoom). */
let cachedOsDevicePixelRatio: number | null = null;

export function getCachedOsDevicePixelRatio(): number | null {
  return cachedOsDevicePixelRatio;
}

/** @internal test helper */
export function __setCachedOsDevicePixelRatioForTests(
  value: number | null,
): void {
  cachedOsDevicePixelRatio = value;
}

async function refreshOsDevicePixelRatioFromScreen(): Promise<number | null> {
  const getScreenDetails = (
    window as Window & {
      getScreenDetails?: () => Promise<ScreenDetailsLike>;
    }
  ).getScreenDetails;
  if (typeof getScreenDetails !== "function") return null;

  try {
    const details = await getScreenDetails();
    const dpr = details.currentScreen?.devicePixelRatio;
    if (Number.isFinite(dpr) && (dpr as number) > 0) {
      cachedOsDevicePixelRatio = dpr as number;
      return cachedOsDevicePixelRatio;
    }
  } catch {
    // Permission denied or API unavailable — keep prior cache / sync fallback.
  }
  return cachedOsDevicePixelRatio;
}

function resolveOsDevicePixelRatioSync(): number {
  if (
    cachedOsDevicePixelRatio != null &&
    cachedOsDevicePixelRatio > 0
  ) {
    return cachedOsDevicePixelRatio;
  }
  return estimateOsDevicePixelRatio(
    window.devicePixelRatio || 1,
    window.outerWidth,
    window.innerWidth,
  );
}

/** Apply compensation to the scale wrapper (falls back to `<html>`). */
export function applyOsScaleCompensation(
  root: HTMLElement | null = document.getElementById(OS_SCALE_ROOT_ID),
  hints: OsScaleNavigatorHints = readNavigatorHints(),
  osDevicePixelRatio: number = resolveOsDevicePixelRatioSync(),
): number {
  const target = root ?? document.documentElement;
  const osDpr = osScaleFactor(
    osDevicePixelRatio,
    shouldCompensateOsScale(hints),
  );
  const inv = 1 / osDpr;
  const windowDpr = window.devicePixelRatio || 1;
  const zoom = browserZoomFactor(windowDpr, osDpr);

  // Drop any previous html zoom from an older approach.
  document.documentElement.style.removeProperty("zoom");

  document.documentElement.dataset.osScaleDpr = String(osDpr);
  document.documentElement.dataset.browserZoom = String(zoom);

  if (osDpr === 1) {
    clearScaleStyles(target);
    document.documentElement.style.removeProperty("overflow");
    document.body?.style.removeProperty("overflow");
    setViewportUnitVars(1);
    document.documentElement.dataset.osScaleZoom = "1";
    return 1;
  }

  // Layout at 100%-OS-scale CSS size, then paint back down to the window.
  // Browser zoom is intentionally NOT folded in — Ctrl+/- still grows type.
  target.style.transformOrigin = "0 0";
  target.style.transform = `scale(${inv})`;
  target.style.width = `${osDpr * 100}%`;
  target.style.height = `${osDpr * 100}%`;
  target.style.removeProperty("zoom");

  document.documentElement.style.overflow = "hidden";
  if (document.body) document.body.style.overflow = "hidden";

  setViewportUnitVars(osDpr);
  document.documentElement.dataset.osScaleZoom = String(inv);
  return inv;
}

/**
 * Keep compensation in sync when the window moves between monitors with
 * different DPI (or when the OS scale changes). Browser zoom changes only
 * resize the CSS viewport — OS compensation factor stays put so fonts grow.
 */
export function installOsScaleCompensation(
  root: HTMLElement | null = document.getElementById(OS_SCALE_ROOT_ID),
): () => void {
  let media: MediaQueryList | undefined;
  let screenDetails: ScreenDetailsLike | null = null;

  const bindResolutionListener = () => {
    media?.removeEventListener("change", onWindowDprChange);
    media = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    media.addEventListener("change", onWindowDprChange);
  };

  const apply = () => {
    applyOsScaleCompensation(root);
    bindResolutionListener();
  };

  // window.devicePixelRatio changed — may be browser zoom OR monitor move.
  // Refresh screen DPR when possible; compensate from OS DPR only.
  const onWindowDprChange = () => {
    void refreshOsDevicePixelRatioFromScreen().finally(apply);
  };

  const onScreenChange = () => {
    void refreshOsDevicePixelRatioFromScreen().finally(apply);
  };

  const onResize = () => apply();

  apply();
  window.addEventListener("resize", onResize);

  void refreshOsDevicePixelRatioFromScreen().then((dpr) => {
    if (dpr != null) apply();

    const getScreenDetails = (
      window as Window & {
        getScreenDetails?: () => Promise<ScreenDetailsLike>;
      }
    ).getScreenDetails;
    if (typeof getScreenDetails !== "function") return;

    void getScreenDetails()
      .then((details) => {
        screenDetails = details;
        details.addEventListener?.("currentscreenchange", onScreenChange);
        details.addEventListener?.("screenschange", onScreenChange);
      })
      .catch(() => {
        /* permission denied */
      });
  });

  return () => {
    window.removeEventListener("resize", onResize);
    media?.removeEventListener("change", onWindowDprChange);
    screenDetails?.removeEventListener?.("currentscreenchange", onScreenChange);
    screenDetails?.removeEventListener?.("screenschange", onScreenChange);
  };
}
